import { HttpUrlEncodingCodec } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Event, GuardsCheckEnd, NavigationCancel, NavigationCancellationCode, NavigationStart, Router, RouterEvent, RoutesRecognized } from '@angular/router';
import { StorageService } from '@damofire/data-angular-commons';
import { AUTH_PATHS } from '@damofire/data-security';
import { BackendPokerService } from '@damofire/uix-commons';
import { interval } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { environment } from '../../../environments/environment';

export const LNG_STORAGE_KEY = 'tt.lng';

@Injectable()
export class RouterInterceptorService {
  static readonly ROUTER_BACKUP_ROUTE = 'df.navigation.backup';
  private readonly subSink = new SubSink();
  private readonly codec = new HttpUrlEncodingCodec();
  private lastRoutes: string[];

  constructor(
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly backendPoker: BackendPokerService,
  ) {
    this.initSubs();
  }

  private pushHistory(url: string): void {
    if (![AUTH_PATHS.AUTH, AUTH_PATHS.SIGN_IN, AUTH_PATHS.REDIRECT, '/403', '/'].includes(url) && !url.includes('/latest')) {
      console.log(`Pushing ${url} in history.`);
      this.lastRoutes.push(url);
      if (this.lastRoutes.length > environment.appConfig.router.history) {
        this.lastRoutes = this.lastRoutes.slice(1);
      }
    }
  }

  private initSubs(): void {
    this.backendPoker.pokeBackend().then((backendStatus) => {
      if (!backendStatus.db || !backendStatus.storage) {
        this.router.navigate(['502']).catch(console.error);
        return;
      }
    });

    this.subSink.add(
      this.router.events.subscribe(async (e: Event) => {
        if (e instanceof GuardsCheckEnd && !e.shouldActivate) {
          const backupRoute = this.storage.getObject(RouterInterceptorService.ROUTER_BACKUP_ROUTE);
          if (backupRoute) {
            this.storage.removeItem(RouterInterceptorService.ROUTER_BACKUP_ROUTE);
            await this.router.navigate(['/dashboard']);
          } else {
            await this.router.navigate(['/403']);
          }
        } else if (e instanceof NavigationCancel && e.code === NavigationCancellationCode.Redirect) {
          console.log(`Navigation was cancelled, storing route before redirect. [${e.url}]`);

          if (environment.target === 'hosting') {
            const params = new URLSearchParams(window.location.search);
            const lang = params.get('lang');
            if (lang) {
              this.storage.setItem(LNG_STORAGE_KEY, lang);
              const domain = environment.home.match(/\.[a-z]+\.com/)[0];
              document.cookie = `lang=${lang};path=/;domain=${domain}`;
            }
          }
          this.storeUrl(e);
        } else if (e instanceof NavigationStart && !this.lastRoutes?.length && e.url.includes('referer=')) {
          const params = this.router.currentNavigation().extractedUrl.queryParams;
          this.pushHistory(params.referer);
        }
      }),
      interval(1000 * 60 * 5).subscribe(() => this.backendPoker.checkFirestore()),
    );

    // on every route change take the two events of two routes changed(using pairwise)
    // and save the old one in a behavior subject to access it in another component
    // we can use if another component like intro-advertise need the previous route
    // because we need to redirect the user to where he came from.
    this.lastRoutes = [];
    this.subSink.add(
      this.router.events
        .pipe(
          filter((e) => e instanceof RoutesRecognized),
          pairwise(),
        )
        .subscribe((event: [RoutesRecognized, RoutesRecognized]) => this.pushHistory(event[0].urlAfterRedirects)),
    );
  }

  lastVisited(url: string): number {
    return this.lastRoutes.lastIndexOf(url);
  }

  getPreviousRoute(): string {
    return this.lastRoutes[this.lastRoutes.length - 1];
  }

  popPreviousRoute(): string {
    return this.lastRoutes.pop();
  }

  reset(): void {
    this.storage.removeItem(RouterInterceptorService.ROUTER_BACKUP_ROUTE);
    this.subSink.unsubscribe();
    this.initSubs();
  }

  private storeUrl(e: RouterEvent): void {
    const urlComponents = this.codec.decodeKey(e.url).split('?');
    const urlToStore = urlComponents[0];
    console.log(`Evaluating route. [${urlToStore}]`);

    if (![AUTH_PATHS.AUTH, AUTH_PATHS.SIGN_IN, AUTH_PATHS.REDIRECT, '/403', '/'].includes(urlToStore)) {
      let params = null;

      if (urlComponents.length > 1) {
        params = {};
        const queryParams = urlComponents[1].split('&');
        queryParams.forEach((q) => {
          const keyValue = q.split('=');
          params[keyValue[0]] = keyValue[1];
        });
      }

      console.log(`Backing up route for next visit. [${urlToStore}]`);
      if (urlToStore !== '/') {
        this.storage.setObject(RouterInterceptorService.ROUTER_BACKUP_ROUTE, { url: urlToStore, query: params });
      }
    }
  }
}
